import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Menu, X, ShoppingBag, ChevronDown } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { listLibrariesPublic } from '../services/api';
import type { PrivateLibrary } from '../types';
import { useHasProducts } from '../hooks/useHasProducts';
import logoMark from '../assets/bleums-mark.png';

// `shopOnly` entries are hidden while the catalog is empty — a Shop tab
// leading to "no products yet" reads as a broken site.
const navItems = [
  { to: '/home', label: 'Home' },
  { to: '/shop', label: 'Shop', shopOnly: true },
  { to: '/custom-request', label: 'Custom Orders' },
  { to: '/order/status', label: 'Track Order' },
];

const Navbar: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [affiliatedOpen, setAffiliatedOpen] = useState(false);
  const [libraries, setLibraries] = useState<PrivateLibrary[]>([]);
  const affiliatedRef = useRef<HTMLDivElement | null>(null);
  const { itemCount } = useCart();
  const hasProducts = useHasProducts();

  // Hide shop-only entries until something is published.
  const visibleNav = navItems.filter(item => !item.shopOnly || hasProducts);

  useEffect(() => {
    let cancelled = false;
    listLibrariesPublic()
      .then(list => {
        if (!cancelled) setLibraries(list);
      })
      .catch(() => {
        // Non-fatal; just hide the dropdown.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Close the affiliated dropdown on outside-click.
  useEffect(() => {
    if (!affiliatedOpen) return;
    const onClick = (e: MouseEvent) => {
      if (affiliatedRef.current && !affiliatedRef.current.contains(e.target as Node)) {
        setAffiliatedOpen(false);
      }
    };
    window.addEventListener('mousedown', onClick);
    return () => window.removeEventListener('mousedown', onClick);
  }, [affiliatedOpen]);

  return (
    <header className="bg-white border-b border-background-dark sticky top-0 z-40 print-hide">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16">
        {/* The full logo's wordmark is unreadable at header height, so the
            floral mark pairs with live text instead of using the lockup. */}
        {/* min-w-0 + truncate so a narrow phone shortens the wordmark
            rather than pushing the menu button off the screen. */}
        <Link
          to="/home"
          className="flex items-center gap-2 min-w-0"
          aria-label="Bleums Creations — home"
        >
          <img
            src={logoMark}
            alt=""
            aria-hidden="true"
            className="h-9 w-9 sm:h-10 sm:w-10 object-contain shrink-0"
          />
          <span className="font-display text-xl sm:text-2xl text-primary tracking-wide leading-none truncate">
            Bleums Creations
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-6">
          {visibleNav.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `text-sm ${isActive ? 'text-primary font-medium' : 'text-text-light hover:text-primary'}`
              }
            >
              {item.label}
            </NavLink>
          ))}

          {libraries.length > 0 && (
            <div className="relative" ref={affiliatedRef}>
              <button
                type="button"
                onClick={() => setAffiliatedOpen(o => !o)}
                className="inline-flex items-center gap-1 text-sm text-text-light hover:text-primary"
              >
                Affiliated
                <ChevronDown size={14} className={affiliatedOpen ? 'rotate-180 transition' : 'transition'} />
              </button>
              {affiliatedOpen && (
                <div className="absolute right-0 mt-2 w-64 bg-white border border-background-dark rounded shadow-md py-1 z-50">
                  {libraries.map(lib => (
                    <Link
                      key={lib.slug}
                      to={`/private/${lib.slug}`}
                      onClick={() => setAffiliatedOpen(false)}
                      className="block px-3 py-2 text-sm hover:bg-background-dark/40"
                    >
                      <div className="font-medium">{lib.name}</div>
                      {lib.description && (
                        <div className="text-xs text-text-light line-clamp-2">{lib.description}</div>
                      )}
                    </Link>
                  ))}
                  <div className="border-t border-background-dark mt-1 pt-1 px-3 pb-2 text-[10px] text-text-light">
                    Password required to view.
                  </div>
                </div>
              )}
            </div>
          )}

          <Link to="/cart" className="relative inline-flex items-center" aria-label="Cart">
            <ShoppingBag className="w-5 h-5 text-text-light hover:text-primary" />
            {itemCount > 0 && (
              <span className="absolute -top-2 -right-2 bg-primary text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {itemCount}
              </span>
            )}
          </Link>
        </nav>

        {/* Cart stays in the phone header rather than only inside the menu:
            it carries a count, and burying it means a customer can't see
            they have anything in it. */}
        <div className="md:hidden flex items-center gap-1 shrink-0">
          <Link to="/cart" className="relative p-2" aria-label="Cart">
            <ShoppingBag className="w-5 h-5 text-text-light" />
            {itemCount > 0 && (
              <span className="absolute top-0 right-0 bg-primary text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center">
                {itemCount}
              </span>
            )}
          </Link>
          <button
            className="p-2 -mr-2"
            onClick={() => setOpen(o => !o)}
            aria-label="Toggle menu"
            aria-expanded={open}
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <nav className="md:hidden border-t border-background-dark bg-white">
          <ul className="px-4 py-2">
            {visibleNav.map(item => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `block py-2 text-sm ${isActive ? 'text-primary font-medium' : 'text-text-light'}`
                  }
                >
                  {item.label}
                </NavLink>
              </li>
            ))}
            {libraries.length > 0 && (
              <li className="border-t border-background-dark mt-2 pt-2">
                <div className="text-xs uppercase tracking-wide text-text-light pb-1">Affiliated</div>
                {libraries.map(lib => (
                  <Link
                    key={lib.slug}
                    to={`/private/${lib.slug}`}
                    onClick={() => setOpen(false)}
                    className="block py-1 text-sm text-text-light"
                  >
                    {lib.name}
                  </Link>
                ))}
              </li>
            )}
            <li className="border-t border-background-dark mt-2 pt-2">
              <Link
                to="/cart"
                onClick={() => setOpen(false)}
                className="block py-2 text-sm text-text-light"
              >
                Cart {itemCount > 0 ? `(${itemCount})` : ''}
              </Link>
            </li>
          </ul>
        </nav>
      )}
    </header>
  );
};

export default Navbar;
